import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auditUrl } from '../../BaseURL';
import { fmtDate, titleCase } from '../../utils/format';

const CATEGORY_COLOR = {
  auth: 'badge-blue', admin: 'badge-gold', sales: 'badge-green',
  inventory: 'badge-gold', purchase: 'badge-blue', finance: 'badge-green',
  catalog: 'badge-gold',
};

const AuditTrail = () => {
  const me = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } })();
  const isSuper = me?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [cats, setCats] = useState([]);
  const [scopeBranchName, setScopeBranchName] = useState(null);
  const [filters, setFilters] = useState({ action_type: '', category: '', from: '', to: '' });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await axios.get(`${auditUrl}?${params.toString()}`);
      if (res.data?.success) {
        setRows(res.data.results || []);
        setTypes(res.data.action_types || []);
        setCats(res.data.categories || []);
        setScopeBranchName(res.data.scope_branch_name || null);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, []);

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <h2>Audit Trail</h2>
          <p className="muted-sm" style={{ margin: '2px 0 0' }}>
            Every login, sale and admin action is logged here for accountability.
          </p>
        </div>
        <div className="panel-toolbar">
          <select className="f-select" style={{ width: 180 }} value={filters.action_type} onChange={(e) => setFilters({ ...filters, action_type: e.target.value })}>
            <option value="">All actions</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="f-select" style={{ width: 150 }} value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
            <option value="">All categories</option>
            {cats.map((c) => <option key={c} value={c}>{titleCase(c)}</option>)}
          </select>
          <input type="date" className="f-input" style={{ width: 150 }} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          <input type="date" className="f-input" style={{ width: 150 }} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          <button className="btn-primary" onClick={load}>Apply</button>
        </div>
      </div>

      {/* Scope banner — non-super users see this so they know the view is filtered to their branch */}
      {!isSuper && scopeBranchName && (
        <div className="audit-scope-banner">
          <span className="ic">🔒</span>
          <div>
            <strong>Showing activity for {scopeBranchName} only.</strong>
            <p>You can see every action taken in your branch but not other branches. The super administrator sees the full company-wide log.</p>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: 'var(--mute)' }}>Loading…</p> : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                {isSuper && <th>Branch</th>}
                <th>Action</th>
                <th>Category</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                  <td>{r.actor_name || <span style={{ color: 'var(--mute)' }}>System</span>}</td>
                  {isSuper && <td>{r.branch_name || '—'}</td>}
                  <td><code style={{ fontSize: 13 }}>{r.action_type}</code></td>
                  <td>{r.category && <span className={`badge ${CATEGORY_COLOR[r.category] || 'badge-gray'}`}>{titleCase(r.category)}</span>}</td>
                  <td>{r.message || (r.meta ? <code style={{ fontSize: 12, color: 'var(--mute)' }}>{r.meta}</code> : '—')}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={isSuper ? 6 : 5} style={{ color: 'var(--mute)', textAlign: 'center', padding: 32 }}>
                    {Object.values(filters).some((v) => v)
                      ? 'No activity matches the current filters.'
                      : isSuper
                        ? 'No activity has been logged yet.'
                        : `No activity logged for ${scopeBranchName || 'your branch'} yet — actions will appear here as your team works.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditTrail;
